/** The ticketing identity a segment can carry; kept structural so any generated segment fragment satisfies it. */
type TicketedSegment = {
  /** The airline's own record locator for this segment, when the booking supplied one per flight. */
  airlineConfirmationId?: string | null;
  /** The e-ticket document covering this segment, when the booking supplied one per flight. */
  eTicketNumber?: string | null;
};

/** The booking-level values, used when no segment carries its own. */
type BookingLevelIdentity = {
  /** Record locator stored against the whole TripFlight row. */
  airlineConfirmationId?: string | null;
  /** e-Ticket number stored against the whole TripFlight row. */
  eTicketNumber?: string | null;
  /** Our own locator for the booking (TripFlight.sourceId). A "confirmation" equal to it is ours, not an airline's. */
  sourceId: string | null;
};

/** How several carriers' locators or documents are joined into the one value a leg shows. */
const SEPARATOR = ', ';

/** Distinct non-empty values, trimmed, in the order the segments present them. */
function distinctValues(values: (string | null | undefined)[]): string[] {
  return [...new Set(values.map((value) => value?.trim()).filter((value): value is string => Boolean(value)))];
}

/**
 * The record locator and e-ticket to show against one leg.
 *
 * A Sabre PNR spanning several carriers issues a locator and a document per carrier, but the TripFlight row holds
 * one of each for the whole booking, so every leg used to render the same joined string and the same ticket number
 * regardless of who was flying it. Prefer what the segments themselves carry, and only fall back to the row when
 * they carry nothing, which is the case for Duffel, for NDC orders with no accounting items, and for rows booked
 * before segments held this at all. A leg that genuinely connects across two carriers keeps both values: that is a
 * real pair of locators, not the booking-level smear this exists to remove.
 *
 * Our own locator never counts as an airline confirmation, whichever side it came from: CERT and some carriers
 * report the PNR back as the flight's confirmationId, so a leg would otherwise offer the traveler our booking
 * reference a second time under a label promising the airline's.
 */
export function legTicketingIdentity(
  segments: readonly TicketedSegment[],
  bookingLevel: BookingLevelIdentity,
): { airlineConfirmationId: string | null; eTicketNumber: string | null } {
  const tickets = distinctValues(segments.map((segment) => segment.eTicketNumber));

  // The row stores a multi-carrier PNR's locators as one joined string, so split it back apart before filtering.
  const segmentConfirmations = distinctValues(segments.map((segment) => segment.airlineConfirmationId));
  const rowConfirmations = distinctValues((bookingLevel.airlineConfirmationId ?? '').split(SEPARATOR));
  const confirmations = (segmentConfirmations.length > 0 ? segmentConfirmations : rowConfirmations).filter(
    (confirmation) => confirmation !== bookingLevel.sourceId,
  );

  return {
    airlineConfirmationId: confirmations.length > 0 ? confirmations.join(SEPARATOR) : null,
    eTicketNumber: tickets.length > 0 ? tickets.join(SEPARATOR) : (bookingLevel.eTicketNumber ?? null),
  };
}
